/* global gdAlertify */
import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { CONCEPTOS_UMBRAL, formatearMonto, invalidarCacheUmbrales } from "../utils/umbralesValores";

const AYUDA_CONCEPTO = {
    turno: "Valor de turno considerado habitual. Sobre este monto se avisa.",
    colacion: "Colación considerada habitual. Sobre este monto se avisa.",
    movilizacion: "Movilización considerada habitual. Sobre este monto se avisa.",
};

const ConfiguracionUmbrales = () => {
    const [umbrales, setUmbrales] = useState({ turno: "", colacion: "", movilizacion: "" });
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        cargarUmbrales();
    }, []);

    const cargarUmbrales = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get("/configuracion/umbrales/lista");
            const valores = data?.data || {};
            setUmbrales({
                turno: String(valores.turno ?? 0),
                colacion: String(valores.colacion ?? 0),
                movilizacion: String(valores.movilizacion ?? 0),
            });
        } catch (error) {
            console.error("Error al obtener los umbrales", error);
            gdAlertify.notiError("No se pudieron cargar los umbrales.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (concepto, valor) => {
        if (valor !== "" && Number(valor) < 0) return;
        setUmbrales((prev) => ({ ...prev, [concepto]: valor }));
    };

    const handleGuardar = async () => {
        setGuardando(true);
        gdAlertify.showLoader();
        try {
            await axiosInstance.post("/configuracion/umbrales/guardar", {
                umbrales: {
                    turno: Number(umbrales.turno || 0),
                    colacion: Number(umbrales.colacion || 0),
                    movilizacion: Number(umbrales.movilizacion || 0),
                },
            });
            invalidarCacheUmbrales();
            gdAlertify.notiSuccess("Umbrales guardados correctamente.");
        } catch (error) {
            console.error("Error al guardar los umbrales", error);
            gdAlertify.notiError("No se pudieron guardar los umbrales.");
        } finally {
            gdAlertify.hideLoader();
            setGuardando(false);
        }
    };

    return (
        <div className="container mt-4">
            <div className="card shadow">
                <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">
                        <i className="bi bi-speedometer2 me-2"></i>
                        Umbrales de Valores
                    </h4>
                    <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || guardando}>
                        <i className="bi bi-save me-2"></i>
                        Guardar Cambios
                    </button>
                </div>

                <div className="card-body">
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        Define el valor habitual de cada concepto. Si al actualizar valores,
                        crear un adicional o asignar un auxiliar se ingresa un monto más alto que ese
                        promedio, el sistema muestra un aviso.
                        <strong> No impide guardar</strong>: es solo una advertencia por si acaso.
                        Deja un valor en <strong>0</strong> para desactivar su aviso.
                    </div>

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status"></div>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {Object.entries(CONCEPTOS_UMBRAL).map(([concepto, etiqueta]) => {
                                const valor = Number(umbrales[concepto] || 0);
                                const desactivado = valor <= 0;

                                return (
                                    <div className="col-md-4" key={concepto}>
                                        <label className="form-label fw-semibold" htmlFor={`umbral-${concepto}`}>
                                            {etiqueta}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input
                                                id={`umbral-${concepto}`}
                                                type="number"
                                                min="0"
                                                step="1000"
                                                className="form-control"
                                                value={umbrales[concepto]}
                                                onChange={(e) => handleChange(concepto, e.target.value)}
                                            />
                                        </div>
                                        <div className="form-text">{AYUDA_CONCEPTO[concepto]}</div>
                                        {desactivado ? (
                                            <span className="badge bg-secondary mt-1">Aviso desactivado</span>
                                        ) : (
                                            <span className="badge bg-success mt-1">
                                                Avisa sobre ${formatearMonto(valor)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionUmbrales;
